<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <h5 class="mb-0">
          {{ $t('profile.sections.categories') }}
        </h5>
      </div>
      <div v-if="canCreateMaterialCategories" class="d-flex align-items-center">
        <button @click="openAddModal" class="btn btn-sm btn-primary">
          <i class="ti ti-plus me-1"></i>
          {{ $t('common.actions.add') }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <!-- Search and filter bar -->
      <div class="mb-3">
        <div class="input-group">
          <span class="input-group-text"><i class="ti ti-search"></i></span>
          <input
            v-model="searchText"
            type="search"
            class="form-control"
            :placeholder="$t('common.search.placeholder')"
            @keyup.enter="searchCategories"
          />
        </div>
      </div>

      <div v-if="isLoading" class="d-flex justify-content-center py-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') }}</span>
        </div>
      </div>
      <div v-else>
        <div v-if="categories.length === 0" class="text-center py-3 text-muted">
          {{ $t('profile.categories.empty') || 'No categories found. Click "Add" to add categories.' }}
        </div>
        <ul v-else class="list-unstyled d-flex flex-wrap gap-2 my-3">
          <CategoryListItem
            v-for="category in categories"
            :key="category.id"
            :category="category"
            :can-edit-material-categories="canEditMaterialCategories"
            :can-delete-material-categories="canDeleteMaterialCategories"
            @edit="openEditModal"
            @delete="confirmDelete"
            @expand="loadChildCategories"
          />
        </ul>

        <!-- Pagination - Only show when more than pageSize records -->
        <div v-if="totalCategories > pageSize" class="d-flex justify-content-between align-items-center mt-3">
          <div class="small text-muted">
            {{ $t('common.pagination.showing') }} {{ (currentPage - 1) * pageSize + 1 }} 
            {{ $t('common.pagination.to') }} {{ Math.min(currentPage * pageSize, totalCategories) }} 
            {{ $t('common.pagination.of') }} {{ totalCategories }} {{ $t('profile.categories.items') || 'categories' }}
          </div>
          <div>
            <button 
              @click="prevPage" 
              class="btn btn-sm btn-outline-secondary me-1"
              :disabled="currentPage <= 1"
            >
              <i class="ti ti-chevron-left"></i>
            </button>
            <button 
              @click="nextPage" 
              class="btn btn-sm btn-outline-secondary"
              :disabled="currentPage * pageSize >= totalCategories"
            >
              <i class="ti ti-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Category Modal with native Bootstrap implementation -->
    <div class="modal fade" id="categoryModal" tabindex="-1" aria-labelledby="categoryModalLabel" aria-hidden="true" ref="categoryModal">
      <div class="modal-dialog">
        <div class="modal-content position-relative">
          <div v-if="isSaving" class="saving-overlay d-flex align-items-center justify-content-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t('common.saving') }}</span>
            </div>
          </div>
          <div class="modal-header">
            <h5 class="modal-title" id="categoryModalLabel">
              {{ isEditing ? $t('profile.categories.edit') : $t('profile.categories.add') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form-group mb-3">
              <label for="parentCategory" class="form-label">{{ $t('profile.categories.parent') || 'Parent Category' }}</label>
              <div class="el-cascader" ref="cascaderRoot">
                <div class="el-cascader__label" @click="toggleCascader">
                  <span v-if="selectedCascaderPath.length === 0" class="el-cascader__placeholder">
                    {{ $t('profile.categories.noParent') || '-- No Parent --' }}
                  </span>
                  <span v-else class="el-cascader__selected">
                    {{ selectedCascaderPath.map(item => item.name).join(' / ') }}
                  </span>
                  <div class="el-cascader__actions">
                    <button 
                      v-if="selectedCascaderPath.length > 0"
                      type="button"
                      class="btn btn-sm btn-link p-0 me-2 text-danger"
                      @click.stop="clearParentSelection"
                      :title="$t('profile.categories.clearParent') || 'Clear parent category'"
                    >
                      <i class="ti ti-x"></i>
                    </button>
                    <i class="el-icon-arrow-down"></i>
                  </div>
                </div>
                <div v-if="isCascaderOpen" class="el-cascader__dropdown">
                  <div ref="cascaderPanels" class="el-cascader__panels">
                    <div 
                      v-for="(level, index) in cascaderLevels" 
                      :key="index"
                      class="el-cascader__panel"
                    >
                      <div v-if="levelLoading && levelLoadingIndex === index" class="el-cascader__loading">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">{{ $t('common.loading') }}</span>
                        </div>
                      </div>
                      <ul v-else class="el-cascader__menu">
                        <li 
                          v-for="item in level" 
                          :key="item.id"
                          class="el-cascader__menu-item"
                          :class="{
                            'is-active': isItemSelected(item, index),
                            'is-disabled': isEditing && item.id === selectedCategory?.id
                          }"
                          @click="selectCascaderItem(item, index)"
                        >
                          <span>{{ item.code }} - {{ item.name }}</span>
                          <i 
                            v-if="item.isHaveChild" 
                            class="el-icon-arrow-right ti ti-chevron-right"
                          ></i>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <small class="form-text text-muted">
                {{ $t('profile.categories.parentHelp') || 'Optional: Select a parent category' }}
              </small>
            </div>
            
            <div class="form-group mb-3">
              <label for="categoryName" class="form-label">{{ $t('profile.categories.name') }} <span class="text-danger">*</span></label>
               <input
                id="categoryName"
                v-model="categoryForm.name"
                type="text"
                class="form-control"
                :placeholder="$t('profile.categories.namePlaceholder')"
                 :maxlength="categoryNameMax"
                required
              />
               <div v-if="(categoryForm.name || '').length / categoryNameMax >= 0.85" class="text-muted small mt-1">{{ (categoryForm.name || '').length }} / {{ categoryNameMax }}</div>
              <div v-if="errors.name" class="text-danger mt-1">{{ errors.name }}</div>
            </div>
            
            <div class="form-group mb-3">
              <label for="categoryCode" class="form-label">{{ $t('profile.categories.code') }} <span class="text-danger">*</span></label>
               <input
                id="categoryCode"
                v-model="categoryForm.code"
                type="text"
                class="form-control"
                :placeholder="$t('profile.categories.codePlaceholder')"
                 :maxlength="categoryCodeMax"
                required
                @blur="validateCodeUniqueness"
              />
               <div v-if="(categoryForm.code || '').length / categoryCodeMax >= 0.85" class="text-muted small mt-1">{{ (categoryForm.code || '').length }} / {{ categoryCodeMax }}</div>
              <div v-if="errors.code" class="text-danger mt-1">{{ errors.code }}</div>
            </div>
            
            <div class="form-group mb-3">
              <label for="categoryDescription" class="form-label">{{ $t('profile.categories.description') }}</label>
               <textarea
                id="categoryDescription"
                v-model="categoryForm.description"
                class="form-control"
                :placeholder="$t('profile.categories.descriptionPlaceholder')"
                rows="3"
                 :maxlength="categoryDescMax"
               ></textarea>
               <div v-if="(categoryForm.description || '').length / categoryDescMax >= 0.85" class="text-muted small mt-1">{{ (categoryForm.description || '').length }} / {{ categoryDescMax }}</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button 
              v-if="(isEditing && canEditMaterialCategories) || (!isEditing && canEditMaterialCategories)"
              type="button" 
              class="btn btn-info"
              @click="handleSave(false)"
              :disabled="isSaving"
            >
              {{ $t('common.save') }}
            </button>
            <button 
              v-if="(isEditing && canEditMaterialCategories) || (!isEditing && canCreateMaterialCategories)"
              type="button" 
              class="btn btn-primary" 
              @click="handleSave(true)"
              :disabled="isSaving"
            >
              {{ $t('common.saveAndClose') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal with native Bootstrap implementation -->
  </div>
</template>

<script>
// Import necessary Composition API functions if converting
import { MATERIAL_CATEGORY_MAX } from '@/constants/maxLengths';
import materialCategoryService from '@/services/materialCategoryService';
import tenantInfoService from '@/services/tenantInfoService';
import CategoryListItem from './CategoryListItem.vue'; // Import the new component
import Swal from 'sweetalert2'; // Import SweetAlert

export default {
  name: 'ProfileCategoriesCard',
  emits: ['categories-updated'],
  components: {
    CategoryListItem // Register the new component
  },
  props: {
    tenantId: {
      type: String,
      required: true
    },
    canCreateMaterialCategories: {
      type: Boolean,
      default: false
    },
    canEditMaterialCategories: {
      type: Boolean,
      default: false
    },
    canDeleteMaterialCategories: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      loadingParents: false,
      levelLoading: false,
      levelLoadingIndex: 0,
      categories: [],
      isEditing: false,
      selectedCategory: null,
      categoryForm: {
        name: '',
        code: '',
        description: '',
        parentId: ''
      },
      errors: {
        name: '',
        code: ''
      },
      // Cascader data
      cascaderLevels: [[]],
      selectedCascaderPath: [],
      isCascaderOpen: false,
      // Search and pagination
      searchText: '',
      currentPage: 1,
      pageSize: 10,
      totalCategories: 0,
      sorting: 'name asc',
      isSaving: false, // Add saving state
      categoryNameMax: MATERIAL_CATEGORY_MAX.name,
      categoryCodeMax: MATERIAL_CATEGORY_MAX.code,
      categoryDescMax: MATERIAL_CATEGORY_MAX.description,
      // Modal references
      categoryModal: null,
    };
  },
  created() {
    this.searchCategories();
  },
  mounted() {
    // Initialize the modals using native Bootstrap
    if (window.bootstrap) {
      this.categoryModal = new window.bootstrap.Modal(document.getElementById('categoryModal'));
    } else {
      console.error('Bootstrap is not loaded');
    }
  },
  methods: {
    // Update entity counts in store and emit event
    async updateCategoriesCount() {
      try {
        // Get updated counts from API
        const response = await tenantInfoService.getEntityCounts();
        if (response) {
          // Update store with new counts 
          this.$store.commit('updateEntityCounts', response);
          
          // Emit event to notify parent components
          this.$emit('categories-updated', {
            count: this.categories.length,
            totalSize: this.totalCategories
          });
        }
      } catch (error) {
        console.error('Error updating category counts:', error);
      }
    },
    async loadChildCategories(categoryToExpand) {
      // Find the category in the main list (might be nested)
      const findAndLoad = (nodes, id) => {
        for (let node of nodes) {
          if (node.id === id) {
            // Check if children are already loaded or being loaded
            if (node.children && node.children.length > 0 || node.isLoadingChildren) {
              node.isExpanded = !node.isExpanded; // Just toggle expansion state locally
              return true;
            }
            
            node.isLoadingChildren = true; // Add loading state
            node.isExpanded = true; // Expand immediately
            
            materialCategoryService.getChildCategories(id)
              .then(response => {
                node.children = response.map(item => ({
                  ...item,
                  isExpanded: false,
                  children: [],
                  isLoadingChildren: false // Initialize loading state for children
                }));
              })
              .catch(error => {
                console.error('Error loading child categories:', error);
                this.$store.commit('SET_NOTIFICATION', { 
                    message: this.$t('profile.categories.loadChildrenError'), 
                    type: 'error' 
                });
                node.isExpanded = false; // Collapse on error
              })
              .finally(() => {
                 node.isLoadingChildren = false;
              });
            return true; // Category found and loading initiated
          }
          // Recursively search in children
          if (node.children && node.children.length > 0) {
             if (findAndLoad(node.children, id)) return true;
          }
        }
        return false; // Not found in this branch
      };

      findAndLoad(this.categories, categoryToExpand.id);
    },
    async loadCascaderChildren(parent, level) {
      if (!parent.isHaveChild) return;
      
      this.levelLoading = true;
      this.levelLoadingIndex = level + 1;
      
      this.cascaderLevels = this.cascaderLevels.slice(0, level + 1);
      this.cascaderLevels.push([]); // Placeholder for loading state

      try {
        const response = await materialCategoryService.getChildCategories(parent.id);
        const children = response.map(item => ({
          ...item,
          isExpanded: false,
          children: []
        }));
        
        // --- Add Logging ---
        // --- End Logging ---

        if (this.cascaderLevels[level + 1]) {
            this.cascaderLevels[level + 1] = children;
            // Scroll panels to the end after DOM update
            this.$nextTick(() => {
              const panelsContainer = this.$refs.cascaderPanels;
              if (panelsContainer) {
                panelsContainer.scrollLeft = panelsContainer.scrollWidth;
              } else {
                console.warn('Cascader panels container ref not found for scrolling.'); // Log if ref is missing
              }
            });
        } else {
            console.warn(`Cascader level ${level + 1} no longer exists, cannot place children.`); 
        }
      } catch (error) {
        console.error('Error loading cascader child categories:', error);
        this.$store.commit('SET_NOTIFICATION', { 
            message: this.$t('profile.categories.loadChildrenError'), 
            type: 'error' 
        });
        // Attempt to remove the loading level on error
        this.cascaderLevels = this.cascaderLevels.slice(0, level + 1);
      } finally {
        this.levelLoading = false;
      }
    },
    toggleCascader() {
      this.isCascaderOpen = !this.isCascaderOpen;
    },
    handleOutsideClick(event) {
      const root = this.$refs.cascaderRoot;
      if (!this.isCascaderOpen) return;
      if (root && !root.contains(event.target)) {
        this.isCascaderOpen = false;
      }
    },
    async selectCascaderItem(item, level) {
      if (this.isEditing && item.id === this.selectedCategory?.id) return;

      // --- Add Logging ---
      // --- End Logging ---

      this.selectedCascaderPath = this.selectedCascaderPath.slice(0, level);
      this.selectedCascaderPath.push(item);

      this.categoryForm.parentId = item.id;

      if (item.isHaveChild) {
        await this.loadCascaderChildren(item, level);
      } else {
        this.cascaderLevels = this.cascaderLevels.slice(0, level + 1);
        this.isCascaderOpen = false;
      }
    },
    isItemSelected(item, level) {
      return this.selectedCascaderPath[level]?.id === item.id;
    },
    async searchCategories() {
      this.isLoading = true;
      try {
        const searchParams = {
          searchText: this.searchText,
          page: this.currentPage,
          pageSize: this.pageSize,
          sorting: this.sorting,
          parentId: null // Explicitly search for root categories
        };
        
        const response = await materialCategoryService.searchCategories(searchParams);
        // Reset expansion and children state on new search/pagination
        this.categories = response.items.map(item => ({
          ...item,
          isExpanded: false,
          children: [],
          isLoadingChildren: false // Add loading state
        })) || [];
        this.totalCategories = response.totalCount || 0;
      } catch (error) {
        console.error('Error searching categories:', error);
        this.$store.commit('SET_NOTIFICATION', { 
            message: this.$t('profile.categories.loadError') || 'Failed to load categories', 
            type: 'error' 
        });
      } finally {
        this.isLoading = false;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.searchCategories();
      }
    },
    nextPage() {
      if (this.currentPage * this.pageSize < this.totalCategories) {
        this.currentPage++;
        this.searchCategories();
      }
    },
    async openAddModal() {
      this.isEditing = false;
      this.resetForm();
      this.selectedCascaderPath = [];
      this.isCascaderOpen = false;

      // Always reload root categories when opening for 'Add'
      await this.loadCascaderRootCategories(); 
      // Reset levels based on the fresh load
      this.cascaderLevels = this.cascaderLevels.length > 0 ? [this.cascaderLevels[0]] : [[]];

      if (this.categoryModal) {
        this.categoryModal.show();
      } else {
        console.error('Category modal not initialized');
      }
    },
    async openEditModal(category) {
      this.isEditing = true;
      this.selectedCategory = { ...category }; 
      this.categoryForm = {
        name: category.name,
        code: category.code,
        description: category.description || '',
        parentId: category.parentId || ''
      };
      
      this.selectedCascaderPath = [];
      this.isCascaderOpen = false;
      
      // Always reload root categories first when opening for 'Edit'
      await this.loadCascaderRootCategories(); 
      // Reset levels based on the fresh load
      this.cascaderLevels = this.cascaderLevels.length > 0 ? [this.cascaderLevels[0]] : [[]];

      // Now load the specific parent path if needed
      if (category.parentId) {
        await this.loadParentPathForEdit(category);
      }
       
      if (this.categoryModal) {
        this.categoryModal.show();
      } else {
        console.error('Category modal not initialized');
      }
    },
    async loadParentPathForEdit(category) {
        if (!category || !category.parentId) {
            this.selectedCascaderPath = [];
            return;
        }

        this.levelLoading = true; 
        this.selectedCascaderPath = []; 
        const parentPath = [];

        // Recursive function to fetch parent details
        const fetchParent = async (currentParentId) => {
            if (!currentParentId) {
                return; // Reached the root or invalid ID
            }
            try {
                const parentCategory = await materialCategoryService.getCategoryById(currentParentId);
                // Prepend to build the path from root downwards
                parentPath.unshift(parentCategory);
                // Recursively call for the next parent if one exists
                if (parentCategory.parentId) {
                    await fetchParent(parentCategory.parentId);
                }
            } catch (fetchError) {
                console.error(`Error fetching parent category ${currentParentId}:`, fetchError);
                // Use Vuex store for notification
                this.$store.commit('SET_NOTIFICATION', { 
                    message: `Failed to fetch parent category ${currentParentId}`, 
                    type: 'error' 
                });
                throw new Error('Failed to fetch full parent path.');
            }
        };

        try {
            // Start fetching from the direct parent
            await fetchParent(category.parentId);
            
            this.selectedCascaderPath = parentPath;
            this.categoryForm.parentId = category.parentId; // Ensure form model is set

            // Reset cascader levels, keeping the already loaded root level
            this.cascaderLevels = [this.cascaderLevels[0]];

            // Sequentially load children for each level in the parent path
            for (let i = 0; i < parentPath.length; i++) {
                const currentLevelItem = parentPath[i];
                if (!this.cascaderLevels[i + 1]) {
                   this.cascaderLevels.push([]);
                }
                // Load children for the current item into the next level panel
                // Make sure loadCascaderChildren is robust enough for this context
                await this.loadCascaderChildren(currentLevelItem, i);
            }

        } catch (error) {
            console.error("Error reconstructing parent path:", error);
            // Use Vuex store for notification
            this.$store.commit('SET_NOTIFICATION', { 
                message: error.message || 'Failed to load parent category path.', 
                type: 'error' 
            });
            this.selectedCascaderPath = []; 
            this.cascaderLevels = [this.cascaderLevels[0]]; // Reset to root level
        } finally {
             this.levelLoading = false;       
        }
    },
    confirmDelete(category) {
      this.selectedCategory = category;
      
      // Use SweetAlert instead of the Bootstrap modal
      Swal.fire({
        title: this.$t('profile.categories.deleteConfirmTitle') || 'Confirm Delete',
        html: `
          <p>${this.$t('profile.categories.deleteConfirmText') || 'Are you sure you want to delete category'} "${category.code} - ${category.name}"?</p>
          <p class="text-danger">${this.$t('profile.categories.deleteWarning') || 'This action cannot be undone.'}</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('common.actions.delete') || 'Delete',
        cancelButtonText: this.$t('common.cancel') || 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteCategory();
        }
      });
    },
    clearParentSelection() {
      this.selectedCascaderPath = [];
      this.categoryForm.parentId = '';
      this.isCascaderOpen = false;
    },
    resetForm() {
      this.categoryForm = {
        name: '',
        code: '',
        description: '',
        parentId: ''
      };
      this.errors = {
        name: '',
        code: ''
      };
    },
    validateForm() {
      let isValid = true;
      this.errors = {
        name: '',
        code: ''
      };
      
      if (!this.categoryForm.name.trim()) {
        this.errors.name = this.$t('profile.categories.nameRequired') || 'Name is required';
        isValid = false;
      }
      
      if (!this.categoryForm.code.trim()) {
        this.errors.code = this.$t('profile.categories.codeRequired') || 'Code is required';
        isValid = false;
      }
      
      return isValid;
    },
    async validateCodeUniqueness() {
      // Only check if there's a code entered
      if (!this.categoryForm.code.trim()) {
        return;
      }

      try {
        // Check if the entered code is already used
        const idToUse = this.isEditing ? this.selectedCategory.id : this.tenantId; // Use tenantId for new categories
        
        // Don't check if code hasn't changed in edit mode
        if (this.isEditing && this.categoryForm.code === this.selectedCategory.code) {
          this.errors.code = ''; // Clear potential previous error
          return;
        }
        
        // Added check to ensure idToUse is valid before API call
        if (!idToUse) {
            console.warn('Cannot check code uniqueness without a valid ID (tenantId or categoryId).');
            return; 
        }

        const isUnique = await materialCategoryService.isCodeUnique(
          idToUse,
          this.categoryForm.code
        );
        
        if (!isUnique) {
          this.errors.code = this.$t('profile.categories.codeNotUnique') || 'This code is already in use';
        } else {
          this.errors.code = '';
        }
      } catch (error) {
        console.error('Error checking code uniqueness:', error);
        // Use Vuex store for notification
        this.$store.commit('SET_NOTIFICATION', { 
            message: 'Failed to verify code uniqueness.', 
            type: 'error' 
        });
      }      
    },
    // Helper: Prepare payload for save
    preparePayload() {
      const payload = { ...this.categoryForm };
      if (payload.parentId === '') {
        payload.parentId = null;
      }
      return payload;
    },

    // Helper: Save category (create or update)
    async saveCategory(payload) {
      if (this.isEditing && this.selectedCategory?.id) {
        const savedCategory = await materialCategoryService.updateCategory(this.selectedCategory.id, payload);
        this.$store.commit('SET_NOTIFICATION', { 
          message: this.$t('profile.categories.updateSuccess') || 'Category updated successfully', 
          type: 'success' 
        });
        return savedCategory;
      }
      
      const savedCategory = await materialCategoryService.createCategory(payload);
      this.$store.commit('SET_NOTIFICATION', { 
        message: this.$t('profile.categories.createSuccess') || 'Category created successfully', 
        type: 'success' 
      });
      return savedCategory;
    },

    // Helper: Handle error during save
    handleSaveError(error) {
      console.error('Error saving category:', error);
      const apiErrorMessage = error.response?.data?.message || this.$t('profile.categories.saveError');
      this.$store.commit('SET_NOTIFICATION', { 
        message: apiErrorMessage || 'Failed to save category', 
        type: 'error' 
      });
    },

    // Helper: Switch to edit mode after creating
    switchToEditMode(savedCategory) {
      if (!savedCategory) return;
      
      this.isEditing = true;
      this.selectedCategory = { ...savedCategory };
      this.categoryForm = {
        name: savedCategory.name,
        code: savedCategory.code,
        description: savedCategory.description || '',
        parentId: savedCategory.parentId || ''
      };
    },

    // Helper: Update category state after editing
    updateCategoryState(savedCategory) {
      if (savedCategory) {
        this.selectedCategory = { ...savedCategory };
      }
    },

    // Helper: Handle post-save actions
    async handlePostSaveActions(savedCategory, shouldClose) {
      await this.searchCategories();
      await this.loadCascaderRootCategories();
      
      if (shouldClose) return;
      
      if (!this.isEditing) {
        this.switchToEditMode(savedCategory);
      } else {
        this.updateCategoryState(savedCategory);
      }
    },

    async handleSave(shouldClose = false) { 
      if (!this.validateForm()) {
        return;
      }
      
      await this.validateCodeUniqueness();
      if (this.errors.code) {
        return; 
      }
      
      this.isSaving = true; 
      let savedCategory = null;
      
      try {
        const payload = this.preparePayload();
        savedCategory = await this.saveCategory(payload);
        await this.updateCategoriesCount();

        if (shouldClose && this.categoryModal) {
          this.categoryModal.hide(); 
        }
      } catch (error) {
        this.handleSaveError(error);
        return;
      } finally {
        this.isSaving = false; 
      }
      
      if (savedCategory) {
        try {
          await this.handlePostSaveActions(savedCategory, shouldClose);
        } catch (reloadError) {
          console.error('Error reloading data after save:', reloadError);
          this.$store.commit('SET_NOTIFICATION', { 
            message: 'Failed to reload category list after saving.', 
            type: 'error' 
          });
        }
      }
    },
    async deleteCategory() {
      if (!this.selectedCategory) return;
      
      try {
        // Use a loading state in SweetAlert
        Swal.fire({
          title: this.$t('profile.categories.deleting') || 'Deleting...',
          html: this.$t('profile.categories.deletingMessage') || 'Please wait while the category is being deleted.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        // Use the actual ID for deletion
        await materialCategoryService.deleteCategory(this.selectedCategory.id); 
        
        // Show success message
        Swal.fire({
          title: this.$t('profile.categories.deleteSuccessTitle') || 'Deleted!',
          text: this.$t('profile.categories.deleteSuccess') || 'Category deleted successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        
        // Use Vuex notification as well
        this.$store.commit('SET_NOTIFICATION', { 
            message: this.$t('profile.categories.deleteSuccess') || 'Category deleted successfully', 
            type: 'success' 
        });
        
        // Refresh the categories list
        this.currentPage = 1;
        await this.searchCategories();
        
        // Update entity counts after successful delete
        await this.updateCategoriesCount();

      } catch (error) {
        console.error('Error deleting category:', error);
        // Extract specific error message from API response if available
        let apiErrorMessage = this.$t('profile.categories.deleteError'); // Default message
        if (error.response?.data?.error?.message) {
           apiErrorMessage = error.response.data.error.message; 
        } else if (error.response?.data?.message) { // Fallback for other potential formats
           apiErrorMessage = error.response.data.message;
        }
        
        // Show error with SweetAlert
        Swal.fire({
          title: this.$t('profile.categories.deleteErrorTitle') || 'Error!',
          text: apiErrorMessage || 'Failed to delete category',
          icon: 'error'
        });
        
        // Use Vuex store for notification
        this.$store.commit('SET_NOTIFICATION', { 
            message: apiErrorMessage || 'Failed to delete category', 
            type: 'error' 
        });
      }
    },
    async loadCascaderRootCategories() {
      this.loadingParents = true; // Use for cascader loading state
      this.levelLoading = true;
      this.levelLoadingIndex = 0;
      try {
        const response = await materialCategoryService.searchCategories({
          page: 1,
          pageSize: 100, // Adjust as needed for root categories
          sorting: 'name asc'
        });
        const rootCategories = response.items.map(item => ({
          ...item,
          isExpanded: false,
          children: [] // Ensure children array exists
        }));
        this.cascaderLevels = [rootCategories]; // Set first level of cascader
      } catch (error) {
        console.error('Error loading root categories for cascader:', error);
        this.$store.commit('SET_NOTIFICATION', { 
            message: this.$t('profile.categories.loadParentError'), 
            type: 'error' 
        });
      } finally {
        this.loadingParents = false;
        this.levelLoading = false;
      }
    },
  }
  ,
  watch: {
    isCascaderOpen(newVal) {
      if (typeof window !== 'undefined') {
        if (newVal) {
          document.addEventListener('mousedown', this.handleOutsideClick, true);
        } else {
          document.removeEventListener('mousedown', this.handleOutsideClick, true);
        }
      }
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('mousedown', this.handleOutsideClick, true);
    }
  }
};
</script>

<style scoped>
.badge {
  font-size: 90%;
}

.badge button {
  background: transparent;
}

.badge button:hover {
  opacity: 0.7;
}

.bg-light-secondary {
  background-color: #f8f9fa;
}

.parent-category-tree {
  background: #fff;
}

.parent-category-item {
  margin-bottom: 0.5rem;
}

.parent-category-item:last-child {
  margin-bottom: 0;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

.el-cascader {
  position: relative;
  display: inline-block;
  width: 100%;
}

.el-cascader__label {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  cursor: pointer;
}

.el-cascader__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.el-cascader__placeholder {
  color: #6c757d;
}

.el-cascader__selected {
  color: #495057;
}

.el-icon-arrow-down {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.3s;
}

.el-cascader__dropdown {
  position: absolute;
  z-index: 1000;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
  margin-top: 4px;
  width: max-content; 
  max-width: 100%; 
  max-height: 300px;
}

.el-cascader__panels {
  display: flex;
  flex-wrap: nowrap;
  max-height: 300px;
  overflow-x: auto;
  overflow-y: hidden;
}

.el-cascader__panel {
  min-width: 180px;
  max-width: 220px;
  box-sizing: border-box;
  border-right: 1px solid #e4e7ed;
  height: 100%; 
  overflow-y: auto;
}

.el-cascader__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.el-cascader__menu {
  margin: 0;
  padding: 0;
  list-style: none;
}

.el-cascader__menu-item {
  position: relative;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #606266;
}

.el-cascader__menu-item:hover {
  background-color: #f5f7fa;
}

.el-cascader__menu-item.is-active {
  color: #409eff;
  font-weight: 700;
}

.el-cascader__menu-item.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.el-icon-arrow-right {
  position: absolute;
  right: 1rem;
  color: #c0c4cc;
}

.saving-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.6);
  z-index: 1051; /* Above modal content */
}
</style> 